// ✅ WorkerNavbar.js — Clean top nav for desktop (My Jobs + Applied Jobs only)
import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Mail, LogOut } from "lucide-react";
import axios from "axios";

const WorkerNavbar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [openJobs, setOpenJobs] = useState(false);
  const [openAccount, setOpenAccount] = useState(false);
  const [workerName, setWorkerName] = useState("Worker");
  const [photoUrl, setPhotoUrl] = useState("");

  const API_WRK = process.env.REACT_APP_API_WRK;
  const API_HOST = API_WRK?.replace("/api/worker", "") || "";

  const workerId = localStorage.getItem("workerId");

  // ⭐ ALWAYS LOAD WORKER FROM BACKEND (no localStorage bullshit)
  useEffect(() => {
    if (!workerId) return;

    axios
      .get(`${API_WRK}/profile/${workerId}`)
      .then((res) => {
        const data = res.data;
        if (data?.name) setWorkerName(data.name);
        if (data?.profilePhotoUrl)
          setPhotoUrl(`${API_HOST}${data.profilePhotoUrl}`);
      })
      .catch((err) =>
        console.error("❌ Failed to fetch worker for navbar:", err)
      );
  }, [workerId, API_WRK, API_HOST]);

  const isActive = (path) =>
    location.pathname === path || location.pathname.startsWith(path);

  const menuItem =
    "px-3 py-2 rounded-md hover:bg-white/10 transition-colors";

  return (
    <header className="bg-gradient-to-r from-blue-600 via-indigo-500 to-purple-600 text-white shadow-md">
      <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
        {/* Left: Brand */}
        <div className="flex items-center gap-3">
          <h1
            onClick={() => navigate("/dashboard")}
            className="text-2xl font-bold tracking-tight cursor-pointer"
          >
            BYN Worker
          </h1>
        </div>

        {/* Middle: Navigation */}
        <nav className="hidden md:flex items-center gap-2 text-sm font-medium">
          <Link
            to="/dashboard"
            className={`${menuItem} ${
              isActive("/dashboard") ? "bg-white/15" : ""
            }`}
          >
            Home
          </Link>

          {/* Jobs dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setOpenJobs(true)}
            onMouseLeave={() => setOpenJobs(false)}
          >
            <button className={`${menuItem} flex items-center gap-1`}>
              Jobs <ChevronDown size={16} />
            </button>

            <AnimatePresence>
              {openJobs && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute mt-2 w-48 bg-white text-gray-800 rounded-lg shadow-lg overflow-hidden"
                >
                  <Link
                    to="/jobs"
                    className="block px-4 py-2 hover:bg-gray-100"
                  >
                    Jobs Hub
                  </Link>
                  <Link
                    to="/my-jobs"
                    className="block px-4 py-2 hover:bg-gray-100"
                  >
                    My Jobs
                  </Link>
                  <Link
                    to="/applied-jobs"
                    className="block px-4 py-2 hover:bg-gray-100"
                  >
                    Applied Jobs
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          <Link
            to="/opportunities"
            className={`${menuItem} ${
              isActive("/opportunities") ? "bg-white/15" : ""
            }`}
          >
            Opportunities
          </Link>

          <Link
            to="/tier"
            className={`${menuItem} ${
              isActive("/tier") ? "bg-white/15" : ""
            }`}
          >
            Tier
          </Link>
        </nav>

        {/* Right: Inbox + Account */}
        <div className="flex items-center gap-4">
          <Link
            to="/inbox"
            className="hidden md:inline-flex items-center gap-1 px-3 py-2 rounded-md hover:bg-white/10 transition-colors"
            title="Inbox"
          >
            <Mail size={18} /> Inbox
          </Link>

          <div
            className="relative"
            onMouseEnter={() => setOpenAccount(true)}
            onMouseLeave={() => setOpenAccount(false)}
          >
            <button className="flex items-center gap-2 px-2 py-1 rounded-md hover:bg-white/10">
              {/* 👍 ALWAYS FRECH FROM BACKEND */}
              <img
                src={
                  photoUrl
                    ? photoUrl
                    : "https://via.placeholder.com/36"
                }
                alt="profile"
                className="w-9 h-9 rounded-full border border-white object-cover"
              />

              <ChevronDown size={16} />
            </button>

            <AnimatePresence>
              {openAccount && (
                <motion.div
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -6 }}
                  transition={{ duration: 0.15 }}
                  className="absolute right-0 mt-2 w-56 bg-white text-gray-800 rounded-lg shadow-lg overflow-hidden"
                >
                  <div className="px-4 py-3 border-b">
                    <div className="text-sm text-gray-500">Signed in as</div>
                    <div className="font-semibold truncate">
                      {workerName}
                    </div>
                  </div>

                  <Link
                    to="/worker-profile"
                    className="block px-4 py-2 hover:bg-gray-100"
                  >
                    Profile
                  </Link>
                  <Link
                    to="/documents"
                    className="block px-4 py-2 hover:bg-gray-100"
                  >
                    Documents
                  </Link>
                  <Link
                    to="/payments"
                    className="block px-4 py-2 hover:bg-gray-100"
                  >
                    Payments
                  </Link>
                  <Link
                    to="/settings"
                    className="block px-4 py-2 hover:bg-gray-100"
                  >
                    Settings
                  </Link>
                  <Link
                    to="/public-profile"
                    className="block px-4 py-2 hover:bg-gray-100"
                  >
                    Public Profile
                  </Link>
                  <Link
  to="/help"
  className="block px-4 py-2 hover:bg-gray-100"
>
  Support
</Link>


                  <div className="border-t" />

                  <button
                    onClick={() => {
                      localStorage.clear();
                      window.location.href = "/worker-login";
                    }}
                    className="w-full flex items-center gap-2 px-4 py-2 hover:bg-red-50 text-red-600 font-semibold"
                  >
                    <LogOut size={16} /> Logout
                  </button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
};

export default WorkerNavbar;
